import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Search, ShieldOff, LogOut, Lock, Unlock, X, Tag } from 'lucide-react';
import api from '../../api';
import Avatar from '../../components/Avatar';
import AccountAgreements from '../../components/AccountAgreements';

/** Подсказка у метки: когда человек последний раз заходил и когда метка пропадёт. */
const tagTitle = (tag) => {
    const moment = (value) => (value ? new Date(value).toLocaleDateString('ru-RU') : '');
    const parts = [];
    if (tag.lastSeenAt) parts.push(`последний вход ${moment(tag.lastSeenAt)}`);
    if (tag.expiresAt) parts.push(`пропадёт ${moment(tag.expiresAt)}`);
    return [tag.description, parts.join(', ')].filter(Boolean).join(' — ');
};

const inputClass = "px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10";
const primaryBtn = "bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed";
const secondaryBtn = "bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400";
const dangerBtn = "bg-red-500 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer transition-colors hover:bg-red-600";

const AdminUsersPage = () => {
    const { user: currentUser } = useOutletContext();
    const [users, setUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState('');
    const [alert, setAlert] = useState(null);
    const [selected, setSelected] = useState(null); // детали редактируемого пользователя
    const [allRoles, setAllRoles] = useState([]);
    const [editForm, setEditForm] = useState({ name: '', email: '', roles: [] });

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 5000);
    };

    const errorText = (err, fallback) =>
        (typeof err?.response?.data === 'string' && err.response.data) || fallback;

    const fetchUsers = useCallback(() => {
        api.get('/api/admin/users', { params: { page, size: 20, search } })
            .then(res => {
                setUsers(res.data.content);
                setTotalPages(res.data.totalPages);
            })
            .catch(() => showAlert('error', 'Не удалось загрузить пользователей'));
    }, [page, search]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    useEffect(() => {
        api.get('/api/admin/users/roles')
            .then(res => setAllRoles(res.data))
            .catch(() => setAllRoles(['ROLE_USER', 'ROLE_ADMIN']));
    }, []);

    const openUser = (u) => {
        api.get(`/api/admin/users/${u.id}`)
            .then(res => {
                setSelected(res.data);
                setEditForm({ name: res.data.name || '', email: res.data.email || '', roles: res.data.roles || [] });
            })
            .catch(() => showAlert('error', 'Не удалось загрузить пользователя'));
    };

    const refreshAfter = (promise, successMessage, failMessage) => {
        promise
            .then(() => {
                showAlert('success', successMessage);
                setSelected(null);
                fetchUsers();
            })
            .catch(err => showAlert('error', errorText(err, failMessage)));
    };

    const handleSaveProfile = () => {
        refreshAfter(
            api.put(`/api/admin/users/${selected.id}`, { name: editForm.name, email: editForm.email }),
            'Пользователь обновлен', 'Не удалось сохранить пользователя'
        );
    };

    const handleSaveRoles = () => {
        refreshAfter(
            api.put(`/api/admin/users/${selected.id}/roles`, { roles: editForm.roles }),
            'Роли обновлены', 'Не удалось обновить роли'
        );
    };

    const handleToggleEnabled = () => {
        const action = selected.enabled ? 'заблокировать' : 'разблокировать';
        if (!window.confirm(`Вы уверены, что хотите ${action} пользователя ${selected.email}?`)) return;
        refreshAfter(
            api.put(`/api/admin/users/${selected.id}`, { enabled: !selected.enabled }),
            selected.enabled ? 'Пользователь заблокирован' : 'Пользователь разблокирован',
            'Не удалось изменить статус'
        );
    };

    const handleReset2Fa = () => {
        if (!window.confirm(`Сбросить 2FA у ${selected.email}?`)) return;
        refreshAfter(
            api.post(`/api/admin/users/${selected.id}/reset-2fa`),
            '2FA сброшена', 'Не удалось сбросить 2FA'
        );
    };

    const handleRevokeSessions = () => {
        if (!window.confirm(`Отозвать все сессии устройств у ${selected.email}?`)) return;
        refreshAfter(
            api.delete(`/api/admin/users/${selected.id}/sessions`),
            'Сессии отозваны', 'Не удалось отозвать сессии'
        );
    };

    const toggleRole = (role) => {
        setEditForm(f => ({
            ...f,
            roles: f.roles.includes(role) ? f.roles.filter(r => r !== role) : [...f.roles, role]
        }));
    };

    const isSelf = selected && currentUser && selected.email === currentUser.email;

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-[28px] font-semibold text-gray-900 m-0">Пользователи</h1>
            </div>

            {alert && (
                <div className={`p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 ${alert.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {alert.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    <span>{alert.message}</span>
                </div>
            )}

            <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200">
                <div className="relative mb-4">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        className={`${inputClass} w-full pl-10`}
                        type="text"
                        placeholder="Поиск по email или имени..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                    />
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-gray-500 border-b border-gray-200">
                                <th className="py-2.5 pr-4 font-medium">Пользователь</th>
                                <th className="py-2.5 pr-4 font-medium">Роли</th>
                                <th className="py-2.5 pr-4 font-medium">2FA</th>
                                <th className="py-2.5 font-medium">Статус</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(u => (
                                <tr
                                    key={u.id}
                                    className="border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => openUser(u)}
                                >
                                    <td className="py-3 pr-4">
                                        <div className="flex items-center gap-3">
                                            <Avatar src={u.avatarUrl} name={u.name} email={u.email} size={32} />
                                            <div className="flex flex-col overflow-hidden">
                                                <span className="font-medium text-gray-900 truncate">{u.name || 'Без имени'}</span>
                                                <span className="text-xs text-gray-500 truncate">{u.email}</span>
                                                {/*
                                                    Метки прямо в списке: ради ответа на вопрос
                                                    «кто каким сервисом пользуется» его и открывают.
                                                */}
                                                {u.tags?.length > 0 && (
                                                    <span className="flex flex-wrap gap-1 mt-1">
                                                        {u.tags.map(tag => (
                                                            <span
                                                                key={tag.id}
                                                                title={tagTitle(tag)}
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                                            >
                                                                <Tag size={10} />
                                                                {tag.name}
                                                            </span>
                                                        ))}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-3 pr-4">
                                        <div className="flex flex-wrap gap-1">
                                            {u.roles.map(r => (
                                                <span key={r} className={`px-2 py-0.5 rounded text-xs font-medium ${r === 'ROLE_ADMIN' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {r.replace('ROLE_', '')}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="py-3 pr-4">
                                        {u.twoFaEnabled
                                            ? <span className="text-emerald-600 font-medium">Вкл</span>
                                            : <span className="text-gray-400">Выкл</span>}
                                    </td>
                                    <td className="py-3">
                                        {u.enabled
                                            ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">Активен</span>
                                            : <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700">Заблокирован</span>}
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr><td colSpan="4" className="py-6 text-center text-gray-400">Ничего не найдено</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-4">
                        <button className={secondaryBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>Назад</button>
                        <span className="text-sm text-gray-500">{page + 1} / {totalPages}</span>
                        <button className={secondaryBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Вперед</button>
                    </div>
                )}
            </div>

            {selected && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
                    <div className="bg-white rounded-xl p-6 w-full max-w-[560px] max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="m-0 text-lg font-semibold text-gray-900">
                                {selected.email} {isSelf && <span className="text-sm font-normal text-gray-400">(это вы)</span>}
                            </h3>
                            <button className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-600 p-1" onClick={() => setSelected(null)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Имя</label>
                            <input className={inputClass} type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
                        </div>
                        <div className="mb-4 flex flex-col gap-1.5 text-left">
                            <label className="text-sm font-medium text-gray-700">Email</label>
                            <input className={inputClass} type="text" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
                        </div>
                        <button className={primaryBtn} onClick={handleSaveProfile}>Сохранить</button>

                        <AccountAgreements accountId={selected.id} />

                        <div className="h-px bg-gray-200 -mx-6 my-5"></div>

                        <h4 className="mt-0 mb-3 text-sm font-semibold text-gray-900">Роли</h4>
                        <div className="flex flex-col gap-2 mb-4">
                            {allRoles.map(role => (
                                <label key={role} className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={editForm.roles.includes(role)}
                                        disabled={isSelf && role === 'ROLE_ADMIN'}
                                        onChange={() => toggleRole(role)}
                                    />
                                    <span>{role}</span>
                                    {isSelf && role === 'ROLE_ADMIN' && <span className="text-xs text-gray-400">(нельзя снять с себя)</span>}
                                </label>
                            ))}
                        </div>
                        <button className={primaryBtn} onClick={handleSaveRoles}>Сохранить роли</button>

                        <div className="h-px bg-gray-200 -mx-6 my-5"></div>

                        {selected.socialConnections && selected.socialConnections.length > 0 && (
                            <div className="mb-4">
                                <h4 className="mt-0 mb-2 text-sm font-semibold text-gray-900">Соц. привязки</h4>
                                <div className="flex flex-wrap gap-2">
                                    {selected.socialConnections.map(c => (
                                        <span key={c.id} className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 capitalize">{c.provider}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3">
                            <button className={secondaryBtn + ' flex items-center gap-2'} onClick={handleReset2Fa} disabled={!selected.twoFaEnabled}>
                                <ShieldOff size={16} /> Сбросить 2FA
                            </button>
                            <button className={secondaryBtn + ' flex items-center gap-2'} onClick={handleRevokeSessions}>
                                <LogOut size={16} /> Отозвать сессии
                            </button>
                            {!isSelf && (
                                <button className={selected.enabled ? dangerBtn + ' flex items-center gap-2' : primaryBtn + ' flex items-center gap-2'} onClick={handleToggleEnabled}>
                                    {selected.enabled ? <><Lock size={16} /> Заблокировать</> : <><Unlock size={16} /> Разблокировать</>}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsersPage;
